import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import logo from "../images/Extractor logo.png";
import {
  fontFamily,
  loginButtonWithoutColorStyle,
  registrationTextFieldStyle,
  useSharedValue,
} from "../components/Constants/constants";
import { BASE_URL } from "../App_Settings/app_settings";
import InstaColors from "../components/Constants/constants";
import { Checkbox } from "@mui/material";
import { sendRegistrationEmail } from "../services/emailService";
import {
  getCountryOptions,
  validateMobileNumber,
  checkCountryCodeRequired,
  getCountryInfo,
  formatPhoneNumber,
} from "../Utility/countryCodesUtil";
import "./registration.css";

const Registration = () => {
  const history = useHistory();
  const { isDirectNavigation, updateDirectNavigation } = useSharedValue();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    countryCode: "",
    username: "",
    organizationName: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setConfirmShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCheckboxSelected, setIsCheckboxSelected] = useState(false);
  const [isTermsSelected, setIsTermsSelected] = useState(false);
  const { handleTourCompleted } = useSharedValue();

  // Password requirement states
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });

  // Country code options for dropdown
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountryInfo, setSelectedCountryInfo] = useState(null);

  // Load country options on mount
  useEffect(() => {
    const options = getCountryOptions();
    setCountryOptions(options);
  }, []);

  const validateFields = () => {
    const newErrors = {};

    // Required field validation (exclude mobileNumber and countryCode initially)
    [
      "firstName",
      "lastName",
      "username",
      "organizationName",
      "password",
      "confirmPassword",
    ].forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
      }
    });

    // Country code validation (mandatory)
    const countryCodeCheck = checkCountryCodeRequired(formData.countryCode);
    if (countryCodeCheck.hasError) {
      newErrors.countryCode = countryCodeCheck.errorMessage;
    }

    // Mobile number validation (mandatory)
    if (!formData.mobileNumber || formData.mobileNumber.trim() === "") {
      newErrors.mobileNumber = "Mobile number is required";
    } else {
      // Validate mobile number length for selected country
      const mobileValidation = validateMobileNumber(formData.mobileNumber, formData.countryCode);
      if (!mobileValidation.isValid) {
        newErrors.mobileNumber = mobileValidation.message;
      }
    }

    // Email Validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.username && !emailRegex.test(formData.username)) {
      newErrors.username = "Please enter a valid email address.";
    }

    // Password Validation
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    if (formData.password && !passwordRegex.test(formData.password)) {
      newErrors.password =
        "Password must be 8+ characters, include 1 uppercase, 1 number, and 1 special character.";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    if (!isTermsSelected) {
      newErrors.checkbox = "You must agree to the terms and privacy policy.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkPasswordRequirements = (password) => {
    setPasswordRequirements({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*]/.test(password),
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "password") {
      checkPasswordRequirements(value);
    }

    // Clear error for the field being edited
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleCountryCodeChange = (e) => {
    const dialCode = e.target.value;
    setFormData({ ...formData, countryCode: dialCode });

    // Get country info to show length requirements
    const countryInfo = getCountryInfo(dialCode);
    setSelectedCountryInfo(countryInfo);

    // Clear country code error
    if (errors.countryCode) {
      setErrors({ ...errors, countryCode: "" });
    }

    // Re-validate mobile number if already entered
    if (formData.mobileNumber) {
      const mobileValidation = validateMobileNumber(formData.mobileNumber, dialCode);
      if (!mobileValidation.isValid) {
        setErrors({ ...errors, mobileNumber: mobileValidation.message, countryCode: "" });
      } else {
        setErrors({ ...errors, mobileNumber: "", countryCode: "" });
      }
    }
  };

  const handleMobileNumberChange = (e) => {
    const value = e.target.value;
    // Only allow digits
    const cleanValue = value.replace(/\D/g, "");
    setFormData({ ...formData, mobileNumber: cleanValue });

    // Clear mobile number error
    if (errors.mobileNumber) {
      setErrors({ ...errors, mobileNumber: "" });
    }

    // Real-time validation if country code is selected
    if (formData.countryCode && cleanValue) {
      const mobileValidation = validateMobileNumber(cleanValue, formData.countryCode);
      if (!mobileValidation.isValid) {
        setErrors({ ...errors, mobileNumber: mobileValidation.message });
      } else {
        setErrors({ ...errors, mobileNumber: "" });
      }
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    setIsLoading(true);
    try {
      // Format mobile number with country code for submission
      const fullMobileNumber = formatPhoneNumber(formData.mobileNumber, formData.countryCode);

      const response = await fetch(`${BASE_URL}/Users/createaccount`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.username,
          firstName: formData.firstName,
          lastName: formData.lastName,
          organization: formData.organizationName,
          mobileNumber: fullMobileNumber,
          countryCode: formData.countryCode,
          userSource: "Extractor",
          password: formData.password,
          recevieNotification: isCheckboxSelected,
          mfaEnabled: true,
          mfaMobile: true,
          mfaEmail: true,
        }),
      });

      if (response.ok) {
        const data1 = await response.json();

        // Send registration email if marketing checkbox is selected
        if (isCheckboxSelected) {
          try {
            await sendRegistrationEmail(formData);
          } catch (error) {
            console.error("Error sending registration email:", error);
          }
        }

        // Check if account requires verification
        if (data1.requiresVerification) {
          // Redirect to verification page
          history.push({
            pathname: "/verify-account",
            state: {
              email: formData.username,
              mobileNumber: formatPhoneNumber(formData.mobileNumber, formData.countryCode),
              firstName: formData.firstName,
            },
          });
        } else {
          alert("Your account has been created successfully.");
          localStorage.removeItem("tourCompleted");
          handleTourCompleted(false);
          localStorage.removeItem("SelectedFilesList");
          localStorage.removeItem("notifications_list");
          history.push({
            pathname: "/login",
            state: { someValue: true },
          });
        }
      } else {
        const errorText = await response.text();
        console.error("Error occurred:", errorText);

        const errorMessage = errorText || "Registration failed. Please try again.";
        alert(errorMessage);
      }
    } catch (error) {
      console.error("Error during registration", error);
      alert("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleBack = () => {
    history.goBack();
  };

  return (
    <div
      className="login-div"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/background.png)`,
      }}
    >
      <div className="registration-container card border">
        <h1 className="text-center">
          <img className="logo" src={logo} alt="logo" />
        </h1>

        <div className="form-row">
          <div className="form-field">
            <label className="form-label" htmlFor="firstName">
              First Name<span className="required-field">*</span>
            </label>
            <InputBase
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter first name"
              style={registrationTextFieldStyle}
            />
            {errors.firstName && <span className="error-message">{errors.firstName}</span>}
          </div>
          <div className="form-field">
            <label className="form-label" htmlFor="lastName">
              Last Name<span className="required-field">*</span>
            </label>
            <InputBase
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter last name"
              style={registrationTextFieldStyle}
            />
            {errors.lastName && <span className="error-message">{errors.lastName}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-label" htmlFor="username">
              Email<span className="required-field">*</span>
            </label>
            <InputBase
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter email"
              style={registrationTextFieldStyle}
            />
            {errors.username && <span className="error-message">{errors.username}</span>}
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="organizationName">
              Organization Name<span className="required-field">*</span>
            </label>
            <InputBase
              name="organizationName"
              value={formData.organizationName}
              onChange={handleChange}
              placeholder="Enter organization name"
              style={registrationTextFieldStyle}
            />
            {errors.organizationName && (
              <span className="error-message">{errors.organizationName}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-label" htmlFor="countryCode">
              Country Code<span className="required-field">*</span>
            </label>
            <Autocomplete
              options={countryOptions}
              value={countryOptions.find((opt) => opt.value === formData.countryCode) || null}
              onChange={(event, newValue) => {
                handleCountryCodeChange({
                  target: {
                    name: "countryCode",
                    value: newValue ? newValue.value : "",
                  },
                });
              }}
              getOptionLabel={(option) => option.label || ""}
              renderOption={(props, option) => (
                <li {...props} style={{ fontFamily: fontFamily, fontSize: 14 }}>
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select country code"
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    style: {
                      ...registrationTextFieldStyle,
                      // fontFamily: fontFamily,
                      // fontSize: 13,
                      // height: 40,
                      // padding: '8px 12px',
                      // backgroundColor: '#EAF6FC'
                    },
                  }}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: 300,
                  fontFamily: fontFamily,
                },
              }}
              noOptionsText="No countries found"
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
            {errors.countryCode && <span className="error-message">{errors.countryCode}</span>}
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="mobileNumber">
              Mobile Number<span className="required-field">*</span>
              {selectedCountryInfo && (
                <span style={{ fontSize: 11, color: "#666", marginLeft: 8 }}>
                  ({selectedCountryInfo.lengthInfo})
                </span>
              )}
            </label>
            <InputBase
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleMobileNumberChange}
              placeholder="Enter mobile number"
              style={registrationTextFieldStyle}
              inputProps={{
                maxLength: selectedCountryInfo ? selectedCountryInfo.maxLength : 15,
              }}
            />
            {errors.mobileNumber && <span className="error-message">{errors.mobileNumber}</span>}
            {/* {formData.countryCode && formData.mobileNumber && !errors.mobileNumber && (
              <span style={{ fontSize: 12, color: '#4CAF50', marginTop: 4 }}>
                ✓ {formatPhoneNumber(formData.mobileNumber, formData.countryCode)}
              </span>
            )} */}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-label" htmlFor="password">
              Password<span className="required-field">*</span>
            </label>
            <InputBase
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter password"
              autoComplete="new-password"
              inputProps={{ autoComplete: "new-password" }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? (
                      <Visibility
                        style={{ fontSize: 24, marginRight: 4, color: InstaColors.primary }}
                      />
                    ) : (
                      <VisibilityOff
                        style={{ fontSize: 24, marginRight: 4, color: InstaColors.primary }}
                      />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              style={registrationTextFieldStyle}
            />

            {((formData.password && !Object.values(passwordRequirements).every((req) => req)) ||
              errors.password) && (
              <div className="password-requirements">
                <div className="requirement">
                  {passwordRequirements.length ? (
                    <CheckCircle style={{ color: "#4CAF50", fontSize: 20, marginRight: 8 }} />
                  ) : (
                    <span
                      style={{
                        color: errors.password ? "red" : "#666",
                        marginRight: 8,
                        marginLeft: 6,
                      }}
                    >
                      •
                    </span>
                  )}
                  <span
                    style={{
                      color: passwordRequirements.length
                        ? "#4CAF50"
                        : errors.password
                          ? "red"
                          : "#666",
                    }}
                  >
                    At least 8 characters
                  </span>
                </div>
                <div className="requirement">
                  {passwordRequirements.uppercase ? (
                    <CheckCircle style={{ color: "#4CAF50", fontSize: 20, marginRight: 8 }} />
                  ) : (
                    <span
                      style={{
                        color: errors.password ? "red" : "#666",
                        marginRight: 8,
                        marginLeft: 6,
                      }}
                    >
                      •
                    </span>
                  )}
                  <span
                    style={{
                      color: passwordRequirements.uppercase
                        ? "#4CAF50"
                        : errors.password
                          ? "red"
                          : "#666",
                    }}
                  >
                    At least 1 uppercase letter
                  </span>
                </div>
                <div className="requirement">
                  {passwordRequirements.number ? (
                    <CheckCircle style={{ color: "#4CAF50", fontSize: 20, marginRight: 8 }} />
                  ) : (
                    <span
                      style={{
                        color: errors.password ? "red" : "#666",
                        marginRight: 8,
                        marginLeft: 6,
                      }}
                    >
                      •
                    </span>
                  )}
                  <span
                    style={{
                      color: passwordRequirements.number
                        ? "#4CAF50"
                        : errors.password
                          ? "red"
                          : "#666",
                    }}
                  >
                    At least 1 number
                  </span>
                </div>
                <div className="requirement">
                  {passwordRequirements.specialChar ? (
                    <CheckCircle style={{ color: "#4CAF50", fontSize: 20, marginRight: 8 }} />
                  ) : (
                    <span
                      style={{
                        color: errors.password ? "red" : "#666",
                        marginRight: 8,
                        marginLeft: 6,
                      }}
                    >
                      •
                    </span>
                  )}
                  <span
                    style={{
                      color: passwordRequirements.specialChar
                        ? "#4CAF50"
                        : errors.password
                          ? "red"
                          : "#666",
                    }}
                  >
                    At least 1 special character (!@#$%^&*)
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="confirmPassword">
              Confirm Password<span className="required-field">*</span>
            </label>
            <InputBase
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm password"
              autoComplete="new-password"
              inputProps={{ autoComplete: "new-password" }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={() => setConfirmShowPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? (
                      <Visibility
                        style={{ fontSize: 24, marginRight: 4, color: InstaColors.primary }}
                      />
                    ) : (
                      <VisibilityOff
                        style={{ fontSize: 24, marginRight: 4, color: InstaColors.primary }}
                      />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              style={registrationTextFieldStyle}
            />
            {errors.confirmPassword && (
              <span className="error-message">{errors.confirmPassword || ""}</span>
            )}
          </div>
        </div>

        <div className="checkbox-container">
          <Checkbox
            size="small"
            style={{ color: InstaColors.checkboxColor }}
            checked={isCheckboxSelected}
            onChange={(e) => {
              setIsCheckboxSelected(e.target.checked);
            }}
          />
          <span className="checkbox-label">
            Receive product updates, news, and other marketing communications.
          </span>
        </div>

        <div className="terms-container">
          <Checkbox
            size="small"
            style={{ color: InstaColors.checkboxColor }}
            checked={isTermsSelected}
            onChange={(e) => setIsTermsSelected(e.target.checked)}
          />
          <span className="checkbox-label">
            By creating an account, you agree to our{" "}
            <a
              href="https://www.complyexchange.com/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
            >
              terms
            </a>{" "}
            and{" "}
            <a
              href="https://www.complyexchange.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              privacy policy
            </a>
            .
          </span>
        </div>

        {errors.checkbox && (
          <span className="error-message" style={{ marginRight: "auto", marginLeft: 70 }}>
            {errors.checkbox}
          </span>
        )}

        <div className="button-container">
          <div className="back-button" onClick={handleBack} style={loginButtonWithoutColorStyle}>
            Back
          </div>
          <div className="register-button" onClick={submitForm} disabled={isLoading}>
            {isLoading ? <CircularProgress style={{ color: "#fff" }} size={22} /> : "Register"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registration;
